<template>
    <div class="space-y-6 bg-white rounded-md shadow-sm border border-gray-200 mt-6">
        <div class="sm:col-span-6 sm:flex justify-between items-center border-b border-gray-200 py-4 px-4">
            <div>
                <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">Current Address</h4>
                <p class="text-sm text-gray-500">Please provide information about your current residence.</p>
            </div>
        </div>
        <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 p-4 pt-0">            
            <div class="sm:col-span-6">
                <alert-card type="warning">
                    <h3 class="text-lg font-bold">
                        Living Overseas?
                    </h3>
                    <p>If you are currently living overseas, please use your last U.S. based address. Foreign addresses or military (APO or FPO) or DoD addresses overseas will NOT work.</p>
                </alert-card>
            </div>

            <div class="sm:col-span-6">
                <label for="address-lookup" class="block text-sm font-medium text-gray-700">
                    Address Lookup
                </label>
                <div class="mt-1">
                    <div class="mt-1 relative rounded-md">
                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <font-awesome-icon :icon="faMapMarkerAlt" class="text-gray-400" />
                        </div>
                        <GMapAutocomplete
                            id="address_lookup"
                            ref="autocompleteRef"
                            placeholder="Start typing address..."
                            @place_changed="initAutocomplete"
                            :options="{
                                componentRestrictions: { country: ['US', 'VI', 'AS', 'GU', 'PR', 'MP'] }
                            }"
                            v-bind:class="'px-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border border-gray-300 rounded-md pl-10'"
                            data-demo-target="address_lookup"
                        >
                        </GMapAutocomplete>
                    </div>
                    <p class="text-xs text-gray-500">Please include postal code for accuracy.</p>
                </div>
            </div>

            <div class="sm:col-span-3">
                <label for="address_1" v-bind:class="(form.errors.address_1)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Address 1 <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="address_1" v-model="form.address_1" :disabled="application.status" type="text" @blur="validateField('address_1')" v-bind:class="(form.errors.address_1)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.address_1" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div class="sm:col-span-3">
                <label for="address_2" v-bind:class="(form.errors.address_2)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Address 2
                </label>
                <div class="mt-1">
                    <input id="address_2" v-model="form.address_2" :disabled="application.status" type="text" v-bind:class="(form.errors.address_2)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.address_2" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div class="sm:col-span-2">
                <label for="city" v-bind:class="(form.errors.city)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    City <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="city" v-model="form.city" :disabled="application.status" type="text" v-bind:class="(form.errors.city)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.city" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div class="sm:col-span-2">
                <label for="state" v-bind:class="(form.errors.state)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    State <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <select id="state" v-model="form.state" :disabled="application.status" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm'" :class="application.status ? 'bg-gray-100' : ''">
                        <option value="">-- SELECT STATE --</option>
                        <option v-for="(stateName, stateCode) in states" :key="stateCode" :value="stateCode">{{ stateName }}</option>
                    </select>
                    <p v-text="form.errors.state" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div class="sm:col-span-2">
                <label for="zip_code" v-bind:class="(form.errors.zip_code)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Zip Code <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="zip_code" v-model="form.zip_code" :disabled="application.status" type="text" v-bind:class="(form.errors.zip_code)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'" :class="application.status ? 'bg-gray-100' : ''">
                    <p v-text="form.errors.zip_code" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <!-- Has current landlord section -->
            <div class="sm:col-span-6">
                <!-- Label for current address ownership -->
                <label class="block text-sm font-medium text-gray-700">
                    Do you have a current landlord?  <span class="text-red-700">*</span>
                </label>

                <!-- Radio buttons for current address -->
                <div class="">
                    <label class="inline-flex items-center">
                        <input type="radio" class="block text-sm font-medium text-gray-700" v-model="form.has_current_landlord" :value="1" @blur="validateField('has_current_landlord')">
                        <span class="ml-2">Yes</span>
                    </label>
                    <label class="inline-flex items-center ml-6">
                        <input type="radio" class="block text-sm font-medium text-gray-700" v-model="form.has_current_landlord" :value="0" @blur="validateField('has_current_landlord')">
                        <span class="ml-2">No</span>
                    </label>
                    <p v-text="form.errors.has_current_landlord" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_landlord == 1" class="sm:col-span-2">
                <label for="landlord_name" v-bind:class="(form.errors.landlord_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Landlord Name <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="landlord_name" v-model="form.landlord_name" :error="form.errors.landlord_name" type="text" @blur="validateField('landlord_name')" v-bind:class="(form.errors.landlord_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.landlord_name" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_landlord == 1" class="sm:col-span-2">
                <label for="landlord_phone" v-bind:class="(form.errors.landlord_phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Landlord Phone <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="landlord_phone" v-model="form.landlord_phone" :error="form.errors.landlord_phone" type="text" @blur="validateField('landlord_phone')" v-bind:class="(form.errors.landlord_phone)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.landlord_phone" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_landlord == 1" class="sm:col-span-2">
                <label for="landlord_email" v-bind:class="(form.errors.landlord_email)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Landlord Email <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="landlord_email" v-model="form.landlord_email" :error="form.errors.landlord_email" type="email" @blur="validateField('landlord_email')" v-bind:class="(form.errors.landlord_email)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.landlord_email" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_landlord == 1" class="sm:col-span-3">
                <label for="current_monthly_rent" v-bind:class="(form.errors.current_monthly_rent)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Monthly Rent <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="current_monthly_rent" v-model="form.current_monthly_rent" type="number" step="0.01" min="0" @blur="validateField('current_monthly_rent')" v-bind:class="(form.errors.current_monthly_rent)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.current_monthly_rent" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_landlord == 1" class="sm:col-span-3">
                <label for="current_move_in_date" v-bind:class="(form.errors.current_move_in_date)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Move-in Date <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="current_move_in_date" v-model="form.current_move_in_date" type="date" @blur="validateField('current_move_in_date')" v-bind:class="(form.errors.current_move_in_date)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.current_move_in_date" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <!-- Has current real estate agent section -->
            <div class="sm:col-span-6">
                <!-- Label for current address ownership -->
                <label class="block text-sm font-medium text-gray-700">
                    Do you have a current Real Estate Agent?  <span class="text-red-700">*</span>
                </label>

                <!-- Radio buttons for current address -->
                <div class="">
                    <label class="inline-flex items-center">
                        <input type="radio" class="block text-sm font-medium text-gray-700" v-model="form.has_current_real_estate_agent" :value="1" @blur="validateField('has_current_real_estate_agent')">
                        <span class="ml-2">Yes</span>
                    </label>
                    <label class="inline-flex items-center ml-6">
                        <input type="radio" class="block text-sm font-medium text-gray-700" v-model="form.has_current_real_estate_agent" :value="0" @blur="validateField('has_current_real_estate_agent')">
                        <span class="ml-2">No</span>
                    </label>
                    <p v-text="form.errors.has_current_real_estate_agent" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_real_estate_agent == 1" class="sm:col-span-2">
                <label for="real_estate_agent_name" v-bind:class="(form.errors.real_estate_agent_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Real Estate Agent Name <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="real_estate_agent_name" v-model="form.real_estate_agent_name" :error="form.errors.real_estate_agent_name" type="text" @blur="validateField('real_estate_agent_name')" v-bind:class="(form.errors.real_estate_agent_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.real_estate_agent_name" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_real_estate_agent == 1" class="sm:col-span-2">
                <label for="real_estate_agent_phone" v-bind:class="(form.errors.real_estate_agent_phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Real Estate Agent Phone Number <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="real_estate_agent_phone" v-model="form.real_estate_agent_phone" :error="form.errors.real_estate_agent_phone" type="text" @blur="validateField('real_estate_agent_phone')" v-bind:class="(form.errors.real_estate_agent_phone)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.real_estate_agent_phone" class="text-red-500 text-sm"></p>
                </div>
            </div>

            <div v-if="form.has_current_real_estate_agent == 1" class="sm:col-span-2">
                <label for="real_estate_agent_email" v-bind:class="(form.errors.real_estate_agent_email)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                    Real Estate Agent Email <span class="text-red-700">*</span>
                </label>
                <div class="mt-1">
                    <input id="real_estate_agent_email" v-model="form.real_estate_agent_email" :error="form.errors.real_estate_agent_email" type="email" @blur="validateField('real_estate_agent_email')" v-bind:class="(form.errors.real_estate_agent_email)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                    <p v-text="form.errors.real_estate_agent_email" class="text-red-500 text-sm"></p>
                </div>
            </div>
            
            <div class="sm:col-span-6">
                <label class="block text-sm font-medium text-gray-700">
                    Do you own or rent your current address? <span class="text-red-700">*</span>
                </label>
                <div class="mt-2">
                    <label class="inline-flex items-center">
                        <input type="radio" class="focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300" v-model="form.current_own_rent" value="1">
                        <span class="ml-2 text-sm text-gray-700">Own</span>
                    </label>
                    <label class="inline-flex items-center ml-6">
                        <input type="radio" class="focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300" v-model="form.current_own_rent" value="2">
                        <span class="ml-2 text-sm text-gray-700">Rent</span>
                    </label>
                    <p v-text="form.errors.current_own_rent" class="text-red-500 text-sm"></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faMapMarkerAlt } from '@fortawesome/pro-light-svg-icons'

import AlertCard from "@/Shared/AlertCard.vue";

export default {
    props: {
        form: Object,
        application: Object,
        states: Object,
        validateField: Function,
    },
    components: {
        AlertCard,
    FontAwesomeIcon
  },
    methods: {
        initAutocomplete(place){
            // RAC-9990 — see Pages/V2/Properties/AddProperty.vue for the full story.
            // Surface inline error via form.errors.address_1 (existing template binding).
            if (!place?.address_components) {
                this.form.errors.address_1 = "We couldn't read a full address from that result. Please pick a more specific result from the dropdown — make sure it's a street address, not a business or area.";
                return;
            }
            this.form.errors.address_1 = null;

            let street = null;
            let route = null;
            this.form.city = null;

            place.address_components.forEach(component => {
                const componentType = component.types[0];
                switch (componentType) {
                    case "street_number": {
                        street = `${component.long_name}`;
                        break;
                    }
                    case "route": {
                        route = component.short_name;
                        break;
                    }
                    case "postal_code": {
                        this.form.zip_code = `${component.long_name}`;
                        break;
                    }
                    case "locality":
                        this.form.city = component.long_name;
                        break;
                    case "neighborhood":
                        if (this.form.city === null) {
                            this.form.city = component.long_name;
                        }
                        break;
                    case "administrative_area_level_3":
                        if (this.form.city === null) {
                            this.form.city = component.long_name;
                        }
                        break;
                    case "administrative_area_level_2":
                        this.form.county = component.long_name;
                        break;
                    case "administrative_area_level_1": {
                        this.form.state = `${component.short_name}`;
                        break;
                    }
                    case "country": {
                        if(['VI', 'AS', 'GU', 'PR', 'MP'].includes(component.short_name)) {
                            this.form.state = `${component.short_name}`;
                        }
                        break;
                    }
                }
            });
            if(street === null || route === null) {
                this.form.address_1 = null;
                this.form.setError('address_1', 'Address 1 not found. Enter the full address in Google search.');
            } else {
                this.form.address_1 = `${street} ${route}`
                this.form.clearErrors('address_1');
            }
            // Clear the autocomplete input after processing
            this.$nextTick(() => {
                if (this.$refs.autocompleteRef && this.$refs.autocompleteRef.$refs && this.$refs.autocompleteRef.$refs.input) {
                    this.$refs.autocompleteRef.$refs.input.value = null;
                }
            });
        }
    }
}
</script>